import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { compact, isEqual, isNil, sum } from 'lodash';
import type { DataGridProps } from '../../../data-grid';
import { DataGrid } from '../../../data-grid';
import type { ExportInfo } from '../../types';
import {
  getBodyRows,
  getFooterRows,
  getHeaderRows,
  getThirdPartyExportMessageRow,
  toTypeUnsafeSubject,
} from '../../logic/exportUtils';
import useExportUpdate from '../../logic/useExportUpdate';
import type {
  ColDef,
  ColGroupDef,
  GridApi,
  Column,
  GridReadyEvent,
  IRowNode,
  GridColumnsChangedEvent,
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { type ExcelCell, type CustomBlockTypeEnum, assertNotNil, SpecialCssClasses, useHasFF, Dates } from 'venn-utils';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
  blockAnalysisSubjects,
  blockBenchmarkConfig,
  blockExportMetadata,
  blockMaxSubjects,
  blockSettings,
  isReportState,
  subjectAsOfDate,
  recoilBlockContentSize,
  subjectAsOfDateWithExplicitSubjectIndex,
  type BlockAsOfDateSettingIndices,
  type StudioRequestSubject,
} from 'venn-state';
import { convertScenarioDataToExcel } from '../charts/returns-distribution/utils';
import { useSetBlockSize } from '../../logic/useSetBlockSize';
import { useAgGridStyle } from './AgGridThemeOverrides';
import { generateSampleValueToMeasure, useMeasureGridText, useMeasureHeader } from '../../../utils/grids';
import classNames from 'classnames';
import { isColGroupDef } from '../../logic/typeUtils';
import { useDebouncedCallback } from 'use-debounce';
import { GetColor } from 'venn-ui-kit';
import { useGetBlockSubjectSuperscripts } from '../../../blocks/components/core';
import { Match } from 'effect';
import { getAutoGroupColumnWidthToFit } from './autoGroupColumnFit';

export interface ExportableAnalysisGridProps extends Omit<DataGridProps, 'columnDefs' | 'gridRef'>, ExportInfo {
  columnDefs: (ColDef | ColGroupDef)[] | undefined;
  exportable: boolean;
  selectedRefId: string;
  isCompact: boolean;
  shouldExpand?: (node: IRowNode<unknown>) => boolean;
  /**
   * Used to control height/width setting from a parent component.
   * If unspecified, grid handles "red borders" overflow functionality itself
   * Otherwise, the parent component should handle it.
   *
   * This is necessary when there are other items present in the block, such as PGA chart,
   * where the grid itself cannot know of other objects inside the block.
   * */
  onGridSizeChange?: (height: number, width: number) => void;
  /**
   * When set, renders the grid in a fixed-height virtualized window instead of auto-height.
   * `dataHeightPx` is the height of the data-row band the window must expose — not the height of the
   * whole grid. The grid's own chrome (column header, pinned bottom rows, scroll spacer) is measured
   * and added on top, so the row viewport ends up exactly `dataHeightPx` tall and the window shows
   * precisely the rows the caller's slice was cut to contain.
   */
  fixedHeightWindow?: { dataHeightPx: number; startRowIndex: number };
  /**
   * Column widths to replay verbatim instead of auto-sizing from this instance's own rendered rows.
   * See `syncSharedColumnWidths`.
   */
  columnWidths?: Readonly<Record<string, number>> | undefined;
  /** Reports the widths this instance auto-sized to, for another instance of the same block to replay. */
  onColumnWidthsMeasured?: (widths: Record<string, number>) => void;
  fitAutoGroupColumnToWidth?: boolean;
  cellHorizontalPaddingPx?: number;
}

const getEmptyStateMessage = (blockType: CustomBlockTypeEnum) =>
  blockType === 'SCENARIO' ? 'No inputs selected. Please configure inputs in the settings panel.' : undefined;

const getElementHeight = (element: HTMLElement) => {
  // We use clientHeight rather than getBoundingClientRect().height as the former is fixed regardless
  // of zoom level whereas the latter changes based on the size on screen.  We need something fixed
  // relative to the table size in pixels rather than on-screen-pixel size.
  let height = element.clientHeight;

  const children = element.children;
  for (let i = 0; i < children.length; ++i) {
    height = Math.max(height, getElementHeight(children.item(i) as HTMLElement) ?? 0);
  }
  return height;
};

const getAgGridHeaderGroupHeight = (gridElement: HTMLElement | null) => {
  const headers = gridElement?.querySelectorAll('.ag-header-row-column-group');
  if (!headers) {
    return undefined;
  }
  let maxHeight = 0;
  for (let i = 0; i < headers.length; ++i) {
    const height = getElementHeight(headers.item(i) as HTMLElement);
    maxHeight = Math.max(maxHeight, height);
  }
  return maxHeight;
};
const DEFAULT_EXPORTABLE_GRID_HORIZONTAL_SCROLLBAR_HEIGHT_PX = 6;

const FIXED_HEIGHT_WINDOW_CLASS = 'fixedHeightWindowGrid';
/** Only used for the first paint, before the real chrome height is measured. */
const FALLBACK_GRID_CHROME_PX = 26;

const AUTO_GROUP_COLUMN_ID = 'ag-Grid-AutoColumn';

const fitAutoGroupColumnToViewport = (api: GridApi, gridElement: HTMLElement | null) => {
  const availableWidth = gridElement?.querySelector<HTMLElement>('.ag-body-viewport')?.clientWidth;
  const autoGroupColumn = api.getColumn(AUTO_GROUP_COLUMN_ID);
  if (!availableWidth || !autoGroupColumn) {
    return;
  }
  const currentWidth = autoGroupColumn.getActualWidth();
  const newWidth = getAutoGroupColumnWidthToFit({
    currentWidth,
    minWidth: autoGroupColumn.getMinWidth(),
    totalWidth: sum(api.getAllGridColumns().map((column) => column.getActualWidth())),
    availableWidth,
  });
  if (newWidth !== currentWidth) {
    api.setColumnWidths([{ key: autoGroupColumn, newWidth }]);
  }
};

const ExportableGrid = ({
  inPrintMode,
  exportable,
  onGridSizeChange,
  selectedRefId,
  isCompact,
  shouldExpand,
  onGridReady,
  pinnedBottomRowData,
  columnDefs: rawColumnDefs,
  theme,
  horizontalScrollbarHeight = DEFAULT_EXPORTABLE_GRID_HORIZONTAL_SCROLLBAR_HEIGHT_PX,
  fixedHeightWindow,
  columnWidths,
  onColumnWidthsMeasured,
  fitAutoGroupColumnToWidth,
  cellHorizontalPaddingPx,
  containerStyle,
  ...props
}: ExportableAnalysisGridProps) => {
  const hasHoldingsMetadata = useHasFF('holdings_metadata');
  const gridRef = useRef<AgGridReact | null>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const isReport = useRecoilValue(isReportState);
  const customBlockSettings = useRecoilValue(blockSettings(selectedRefId));
  const exportMetaData = useRecoilValue(blockExportMetadata(selectedRefId));
  const benchmarkConfig = useRecoilValue(blockBenchmarkConfig(selectedRefId));

  /** One of the few rare times we use raw state, because it comes from an ag-grid event. */
  const setRawBlockContentSize = useSetBlockSize(recoilBlockContentSize.rawState(selectedRefId));

  // A fixed-height window measures the virtualized viewport, not the natural content height, so only
  // the unwindowed source-page instance may report size into Recoil's blockContentSize.
  const applyGridSizeChange = useCallback(
    (height: number, width: number) => {
      if (fixedHeightWindow) {
        return;
      }
      if (onGridSizeChange) {
        onGridSizeChange(height, width);
      } else {
        setRawBlockContentSize({
          bounds: {
            height,
            width,
          },
        });
      }
    },
    [fixedHeightWindow, onGridSizeChange, setRawBlockContentSize],
  );

  const commonBenchmarkName =
    benchmarkConfig.type === 'COMMON' && benchmarkConfig.relative ? benchmarkConfig.subject?.name : undefined;

  const customBlockType = customBlockSettings?.customBlockType;
  const currentHeightRef = useRef<number | undefined>(undefined);

  const { rowData, treeData, autoGroupColumnDef } = props;

  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const measureText = useMeasureGridText();
  const measureHeader = useMeasureHeader();
  const columnDefs = useMemo((): typeof rawColumnDefs => {
    const cellWidthGuess = measureText(
      generateSampleValueToMeasure({
        mode: 'currency',
        integerDigits: 3,
        fractionalDigits: 2,
        canBeNegative: true,
      }),
      'normal',
    );

    const processColumnDef = (columnDef: ColDef | ColGroupDef): ColDef | ColGroupDef => {
      if (isColGroupDef(columnDef)) {
        return { ...columnDef, children: columnDef.children.map(processColumnDef) };
      }
      if (columnDef.minWidth || !columnDef.headerName) {
        return columnDef;
      }

      const headerWithSuperscriptText = !isNil(columnDef.headerComponentParams?.subject)
        ? `${columnDef.headerName}${getBlockSubjectSuperscripts({ subject: columnDef.headerComponentParams.subject, isBenchmark: !!columnDef.headerComponentParams.isBenchmark || !!columnDef.headerComponentParams.isCommonBenchmark })}`
        : '';

      return {
        ...columnDef,
        minWidth: Math.max(
          cellWidthGuess,
          measureHeader(columnDef.headerName),
          measureHeader(headerWithSuperscriptText),
        ),
      };
    };
    return rawColumnDefs?.map(processColumnDef);
  }, [measureText, rawColumnDefs, getBlockSubjectSuperscripts, measureHeader]);

  const excelDataFn = useRecoilCallback(
    ({ snapshot }) =>
      (): ExcelCell[][] => {
        if (!exportable) {
          return [getThirdPartyExportMessageRow()];
        }
        const getSubjectToAsOfDateMap = (
          subjects: StudioRequestSubject[],
        ): Record<StudioRequestSubject['id'], number | undefined> =>
          Object.fromEntries(
            subjects
              .map((subject: StudioRequestSubject, index) =>
                Match.value(customBlockType).pipe(
                  Match.whenOr('PERFORMANCE_SUMMARY', 'SCENARIO', () => [
                    subject.id,
                    snapshot.getLoadable(subjectAsOfDate([selectedRefId, toTypeUnsafeSubject(subject)])).valueMaybe(),
                  ]),
                  Match.when('PORTFOLIO_COMPARISON', () => [
                    subject.id,
                    snapshot
                      .getLoadable(
                        subjectAsOfDateWithExplicitSubjectIndex([
                          selectedRefId,
                          toTypeUnsafeSubject(subject),
                          index as BlockAsOfDateSettingIndices,
                        ]),
                      )
                      .valueMaybe(),
                  ]),
                  Match.orElse(() => undefined),
                ),
              )
              .filter((maybeSubject) => !isNil(maybeSubject)),
          );

        if (customBlockType === 'SCENARIO') {
          const maxSubjects = snapshot.getLoadable(blockMaxSubjects(selectedRefId)).valueOrThrow();
          const subjects = snapshot
            .getLoadable(blockAnalysisSubjects(selectedRefId))
            .valueOrThrow()
            .slice(0, maxSubjects);
          // Cast to group column def as scenario analysis is always grouped
          const groupColumnDefs = columnDefs as ColGroupDef[] | undefined;
          const columnChildren = groupColumnDefs?.[1]?.children;
          const studioRequestSubjects: StudioRequestSubject[] = columnChildren
            ? compact(
                columnChildren.map((column) =>
                  isColGroupDef(column)
                    ? column.headerGroupComponentParams.subject
                    : column.headerComponentParams.subject,
                ),
              )
            : [];
          const subjectToAsOfDate = getSubjectToAsOfDateMap(studioRequestSubjects);
          const subjectHeaders = studioRequestSubjects.map((subject) => {
            const name = subject.name;
            const asOfDate = subjectToAsOfDate[subject.id];
            return asOfDate ? `${name} (As of ${Dates.toDayMonthYear(asOfDate)})` : undefined;
          });
          const subjectLabels = columnChildren?.map((column) => assertNotNil(column.headerName)) ?? [];
          return convertScenarioDataToExcel({ subjects, subjectLabels, rowData, commonBenchmarkName, subjectHeaders });
        }

        // skip the first row for public-private asset growth which is historical and non-exportable
        const isPublicPrivateAssetGrowth =
          customBlockType === 'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN' ||
          customBlockType === 'PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES';
        const filteredRowData = isPublicPrivateAssetGrowth ? rowData?.slice(1) : rowData;
        const subjectToAsOfDate = columnDefs
          ? getSubjectToAsOfDateMap(
              columnDefs
                .map((column) =>
                  isColGroupDef(column)
                    ? column.headerGroupComponentParams?.subject
                    : column.headerComponentParams?.subject,
                )
                .filter((maybeSubject) => !isNil(maybeSubject)),
            )
          : {};
        const columnDefsWithAsOfDate = columnDefs?.map((column) => ({
          ...column,
          headerComponentParams: isColGroupDef(column)
            ? undefined
            : {
                ...column.headerComponentParams,
                asOfDate: subjectToAsOfDate[column.headerComponentParams?.subject?.id],
              },
          headerGroupComponentParams: isColGroupDef(column)
            ? {
                ...column.headerGroupComponentParams,
                asOfDate: subjectToAsOfDate[column.headerGroupComponentParams?.subject?.id],
              }
            : undefined,
        }));

        const excelHeaders = getHeaderRows({
          treeData: !!treeData,
          columnDefs: columnDefsWithAsOfDate,
          autoGroupColumnDef,
        });
        const bodyData = [...(filteredRowData ?? []), ...(pinnedBottomRowData ?? [])];
        const bodyExcel = getBodyRows(!!treeData, bodyData, columnDefs, autoGroupColumnDef, gridRef?.current);
        const footerExcel = getFooterRows(customBlockType);
        return [...excelHeaders, ...bodyExcel, ...footerExcel];
      },
    [
      exportable,
      customBlockType,
      treeData,
      columnDefs,
      autoGroupColumnDef,
      rowData,
      pinnedBottomRowData,
      selectedRefId,
      commonBenchmarkName,
    ],
  );

  useExportUpdate({ selectedRefId, exportMetaData, excelDataFn });

  const handleRowExpansion = useCallback(
    () => shouldExpand && gridRef?.current?.api?.forEachNode((node) => node.setExpanded(shouldExpand(node))),
    [shouldExpand],
  );

  const fixedHeightWindowStartRowIndex = fixedHeightWindow?.startRowIndex;
  const lastScrolledWindowRef = useRef<unknown>(undefined);

  // Driven from both grid-ready and an effect, because whichever runs second is the first one with an
  // api to call. `ensureIndexVisible` writes scrollTop, redraws rows and flushes pending frames, so it
  // skips repeats of a position it has already placed.
  const scrollToFixedHeightWindowStart = useCallback(() => {
    const api = gridRef?.current?.api;
    if (fixedHeightWindowStartRowIndex === undefined || !rowData?.length || !api) {
      return;
    }
    const scrollKey = { startRowIndex: fixedHeightWindowStartRowIndex, rowData };
    if (isEqual(lastScrolledWindowRef.current, scrollKey)) {
      return;
    }
    lastScrolledWindowRef.current = scrollKey;
    api.ensureIndexVisible(fixedHeightWindowStartRowIndex, 'top');
  }, [fixedHeightWindowStartRowIndex, rowData]);

  const onGridReadyWithExpansionHandler = useCallback(
    (event: GridReadyEvent) => {
      onGridReady?.(event);
      handleRowExpansion();
      scrollToFixedHeightWindowStart();
    },
    [onGridReady, handleRowExpansion, scrollToFixedHeightWindowStart],
  );

  useEffect(handleRowExpansion, [handleRowExpansion]);
  useEffect(scrollToFixedHeightWindowStart, [scrollToFixedHeightWindowStart]);

  /**
   * Everything the grid renders outside the data-row viewport: column header (which can be two bands
   * deep when columns are grouped), pinned bottom rows, and the horizontal-scroll spacer. Measured as
   * root-minus-viewport rather than derived from a header constant, so it stays a fixed point — the
   * container is sized to `data + chrome`, which makes the viewport exactly `data`, leaving chrome
   * unchanged on the next pass.
   */
  const [chromePx, setChromePx] = useState<number | undefined>(undefined);

  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!fixedHeightWindow || !wrapper || typeof ResizeObserver === 'undefined') return undefined;

    let root: HTMLElement | null = null;
    let viewport: HTMLElement | null = null;
    let observedHeader: HTMLElement | null = null;
    const cached = <T extends HTMLElement>(node: T | null, selector: string): T | null =>
      node?.isConnected ? node : wrapper.querySelector<T>(selector);

    const readChrome = () => {
      root = cached(root, '.ag-root-wrapper');
      viewport = cached(viewport, '.ag-body-viewport');

      // Pinning the container height stops the wrapper from resizing, so the wrapper alone would never
      // report the column header growing (a second header band appearing, or header text wrapping once
      // columns are auto-sized). Observe the header itself so a late height change still lands.
      const header = cached(observedHeader, '.ag-header');
      if (header !== observedHeader) {
        if (observedHeader) observer.unobserve(observedHeader);
        if (header) observer.observe(header);
        observedHeader = header;
      }

      if (!root || !viewport || root.offsetHeight <= 0) return;
      const measured = root.offsetHeight - viewport.offsetHeight;
      if (measured > 0) {
        setChromePx((previous: number | undefined) => (previous === measured ? previous : measured));
      }
    };

    const observer = new ResizeObserver(readChrome);
    observer.observe(wrapper);
    readChrome();

    return () => observer.disconnect();
  }, [fixedHeightWindow]);

  /**
   * AG-Grid auto-sizes a column from the rows it has actually rendered, so a row-virtualized window sizes
   * the tree column to its own slice of rows and drifts away from the full-content instance of the same
   * block. The full-content instance publishes what it settled on; a windowed one replays that.
   */
  const applySharedColumnWidths = useCallback(
    (api: GridApi | undefined, knownColumns?: Column[]) => {
      const columns = knownColumns ?? api?.getAllGridColumns();
      if (!api || !columns || !columnWidths) {
        return;
      }

      const updates = columns.flatMap((column) => {
        const newWidth = columnWidths[column.getColId()];
        return newWidth === undefined || newWidth === column.getActualWidth()
          ? []
          : [{ key: column.getColId(), newWidth }];
      });
      if (updates.length > 0) {
        api.setColumnWidths(updates);
      }
    },
    [columnWidths],
  );

  // The widths arrive from another instance of the same block, so they can land long after this grid's
  // last size or column event — replay them whenever they change rather than only while auto-sizing.
  useEffect(() => {
    applySharedColumnWidths(gridRef?.current?.api);
  }, [applySharedColumnWidths]);

  const syncSharedColumnWidths = (api: GridApi) => {
    const columns = api.getAllGridColumns();
    if (!columns) {
      return;
    }

    if (!columnWidths) {
      onColumnWidthsMeasured?.(
        Object.fromEntries(columns.map((column) => [column.getColId(), column.getActualWidth()])),
      );
      return;
    }

    applySharedColumnWidths(api, columns);
  };

  const autofitAgGridColumns = (
    api: GridApi,
    columnApi: GridApi,
    gridElement: HTMLElement | null,
    autoSizeColumns: boolean,
  ) => {
    const cols = columnApi.getColumns();
    if (autoSizeColumns && !isNil(cols)) {
      const ids = cols.filter((c) => c.getColDef().suppressSizeToFit).map((c) => c.getColId());
      // this resizes the fixed size columns to fit their data, it only needs to be called once per
      // loading of data or grid size change as after that the column width should be fixed
      // also autosize any autocolumns (for tree views) as these do not show up in the col defs
      columnApi.autoSizeColumns([AUTO_GROUP_COLUMN_ID, ...ids], true);
    }
    // this resizes all remaining columns using flex to fit the remaining space
    // we call this on any grid resize event, the autoSizeColumns is only on data or grid config changes
    api.sizeColumnsToFit();
    if (fitAutoGroupColumnToWidth) {
      fitAutoGroupColumnToViewport(api, gridElement);
    }
    syncSharedColumnWidths(api);
    const height = getAgGridHeaderGroupHeight(gridElement);
    height && api.setGridOption('groupHeaderHeight', height);
  };

  /**
   * Debounce added b/c in React 18 with multi-threaded rendering it appears that there is a race condition
   * between when AG-Grid defaults column widths to minWidth and when our resize handler gets called
   * to auto-size columns.  If ours runs first then the AG-Grid minWidths will override our auto-sizing.
   *
   * Debounce appears to fix this and ensure that our auto-sizing runs after AG-Grid has set minWidths.
   */
  const onGridColumnsChanged = useDebouncedCallback((event: GridColumnsChangedEvent) => {
    if (!event.api || !event.api.getAllGridColumns()) {
      return;
    }

    autofitAgGridColumns(event.api, event.api, gridWrapperRef.current, true);

    const width = sum(event.api.getAllGridColumns().map((column: Column) => column.getActualWidth()));
    applyGridSizeChange(currentHeightRef.current ?? 0, width);
  }, 50);

  /**
   * Debounce added b/c in React 18 with multi-threaded rendering it appears that there is a race condition
   * between when AG-Grid defaults column widths to minWidth and when our resize handler gets called
   * to auto-size columns.  If ours runs first then the AG-Grid minWidths will override our auto-sizing.
   *
   * Debounce appears to fix this and ensure that our auto-sizing runs after AG-Grid has set minWidths.
   */
  const onGridSizeChanged = useDebouncedCallback((event) => {
    if (!event.api || !event.api.getAllGridColumns()) {
      return;
    }

    // NOTE: last argument used to be false, as users can't change column widths for now, autosizing is
    // safe to keep always on, and with a debounce performance is very good
    autofitAgGridColumns(event.api, event.api, gridWrapperRef.current, true);

    const width = sum(event.api.getAllGridColumns().map((column: Column) => column.getActualWidth()));
    currentHeightRef.current = event.clientHeight;
    applyGridSizeChange(event.clientHeight ?? 0, width);
  }, 50);

  const agStyle = useAgGridStyle();
  const agGridStyleClassName = agStyle.className;
  // Apply the base theme of alpine and any additional theme-like overrides
  const gridClassName = classNames(agGridStyleClassName, 'ag-theme-alpine', {
    [FIXED_HEIGHT_WINDOW_CLASS]: !!fixedHeightWindow,
  });

  const gridContainerStyle = useMemo(
    (): React.CSSProperties | undefined =>
      cellHorizontalPaddingPx === undefined
        ? containerStyle
        : ({
            ...containerStyle,
            '--ag-cell-horizontal-padding': `${cellHorizontalPaddingPx}px`,
          } as React.CSSProperties),
    [containerStyle, cellHorizontalPaddingPx],
  );

  const fixedHeightGridProps = useMemo(
    ():
      | Pick<
          DataGridProps,
          'domLayout' | 'suppressCellFocus' | 'containerStyle' | 'scrollbarWidth' | 'suppressGroupRowsSticky'
        >
      | undefined =>
      fixedHeightWindow
        ? {
            domLayout: 'normal',
            containerStyle: {
              ...gridContainerStyle,
              height: fixedHeightWindow.dataHeightPx + (chromePx ?? FALLBACK_GRID_CHROME_PX),
            },
            // Continuation slices are non-interactive; without this, cell focus could scroll the fixed window out of alignment.
            suppressCellFocus: true,
            // The source page renders at auto height with no vertical scrollbar. A windowed slice must not
            // reserve space for one either, or its columns get auto-sized against a narrower viewport and no
            // longer line up with the source page's.
            scrollbarWidth: 0,
            // A scrollable viewport makes AG-Grid pin the current tree ancestor as a sticky row over the top of
            // the window, duplicating a row the source page shows in place and covering the slice's own first row.
            suppressGroupRowsSticky: true,
          }
        : undefined,
    [fixedHeightWindow, chromePx, gridContainerStyle],
  );

  return (
    <StyledDataGrid
      /**
       * 'key' is needed to force React to re-create the grid when the grid format changes.
       * Unfortunately some changes like rowHeight aren't recalculated when the class style of the grid
       * changes and there doesn't seem to be another way to force it
       */
      key={agGridStyleClassName}
      {...props}
      columnDefs={columnDefs}
      containerStyle={gridContainerStyle}
      gridRef={gridRef}
      className={gridClassName}
      suppressHorizontalScroll={isReport || inPrintMode}
      refreshedStyling={hasHoldingsMetadata}
      horizontalScrollbarHeight={horizontalScrollbarHeight}
      suppressColumnVirtualisation
      dataGridWrapperRef={gridWrapperRef}
      onGridColumnsChanged={onGridColumnsChanged}
      onGridSizeChanged={onGridSizeChanged}
      onGridReady={onGridReadyWithExpansionHandler}
      overlayNoRowsTemplate={getEmptyStateMessage(customBlockType)}
      pinnedBottomRowData={pinnedBottomRowData}
      stopEditingWhenCellsLoseFocus
      {...fixedHeightGridProps}
    />
  );
};

const StyledDataGrid = styled(DataGrid)<{
  suppressHorizontalScroll?: boolean;
  refreshedStyling?: boolean;
}>`
  /*
  * A fixed-height window is a page slice, not a scrollable region: the reader must not be able to
  * scroll it out of alignment with the page it was cut for, and the wheel must keep reaching the page
  * canvas behind it. AG-Grid still positions the window programmatically via scrollTop, which keeps
  * working under overflow: hidden.
  */
  &.${FIXED_HEIGHT_WINDOW_CLASS} .ag-body-viewport {
    overflow: hidden !important;
  }

  .ag-cell-value {
    text-overflow: unset;
  }
  .ag-header-group-cell-with-group > .ag-header-cell-comp-wrapper-limited-height {
    max-height: none !important;
  }

  ${(props) =>
    props.refreshedStyling &&
    css`
      .ag-row-even:not(.ag-row-hover) {
        background-color: ${GetColor.GreyScale.Grey10};
      }

      .ag-row-odd:not(.ag-row-hover) {
        background-color: ${GetColor.White};
      }

      /* Custom styles for Changes column cells */

      .ag-changes-cell-even:not(.ag-row-hover *) {
        background-color: ${GetColor.GreyScale.Grey20};
      }

      .ag-changes-cell-odd:not(.ag-row-hover *) {
        background-color: ${GetColor.GreyScale.Grey10};
      }
    `};

  ${(props) =>
    props.suppressHorizontalScroll &&
    css`
      .ag-center-cols-viewport {
        overflow-x: clip !important;
      }
    `};

  .${SpecialCssClasses.ExportAsImage} & .ag-body-horizontal-scroll {
    display: none;
  }
`;

export default ExportableGrid;
